
import React from "react";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import PaymentFlowButton from "@/components/PaymentFlowButton";
import { SubscriptionPlan } from "@/context/AuthContext";
import { isAndroidTWA } from "@/utils/runtimeContext";
import AndroidNeutralNotice from "@/components/legal/AndroidNeutralNotice";

type PricingFeature = {
  title: string;
  included: boolean;
};

type PricingPeriod = {
  name: string;
  price: string;
  billingLabel: string;
  discount?: string;
};

type PricingCardProps = {
  title: string;
  description: string;
  features: PricingFeature[];
  buttonText: string;
  buttonLink?: string; // Optional now as we might use PaymentFlowButton
  isPopular?: boolean;
  pricingPeriods?: PricingPeriod[];
  selectedPeriod?: string;
  // Either price or monthlyPrice can be used (for backward compatibility)
  price?: string;
  monthlyPrice?: string;
  planType?: SubscriptionPlan; // Add plan type for payment integration
  usePaymentFlow?: boolean; // Toggle for using payment flow
};

const PricingCard = ({
  title,
  price,
  monthlyPrice,
  description,
  features,
  buttonText,
  buttonLink,
  isPopular = false,
  pricingPeriods,
  selectedPeriod = "monthly",
  planType = "basic",
  usePaymentFlow = true,
}: PricingCardProps) => {
  if (isAndroidTWA()) {
    return <AndroidNeutralNotice />;
  }

  // Use either price or monthlyPrice (price takes precedence if both are provided)
  const basePrice = price || monthlyPrice || "";
  
  // Find the selected pricing period or default to base price
  const activePeriod = pricingPeriods?.find(period => period.name === selectedPeriod);
  const displayPrice = activePeriod?.price || basePrice;
  const displayBilling = activePeriod?.billingLabel || (
    title.includes("Mensal") ? "/mês" : 
    title.includes("Trimestral") ? "/trimestre" : 
    title.includes("Anual") ? "/ano" : 
    "/mês"
  );

  // Map plan title to subscription plan type
  const getPlanType = (): SubscriptionPlan => {
    if (title.includes("Mensal")) return "basic";
    if (title.includes("Trimestral")) return "premium";
    if (title.includes("Anual")) return "vip";
    return planType;
  };

  return (
    <div className={`flex flex-col p-6 rounded-xl ${isPopular ? 'border-2 border-accent relative shadow-lg' : 'border border-border'} bg-card h-full`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-semibold">
          Recomendado
        </div>
      )}
      <div className="mb-5">
        <h3 className="text-xl font-semibold">{title}</h3>
        <p className="text-muted-foreground mt-2 text-sm">{description}</p>
      </div>
      <div className="mb-6">
        <p className="inline-flex items-baseline">
          <span className="text-3xl font-bold">{displayPrice}</span>
          <span className="ml-1 text-muted-foreground text-sm">{displayBilling}</span>
        </p>
        {activePeriod?.discount && (
          <div className="mt-1">
            <span className="text-sm text-green-600 font-medium">{activePeriod.discount}</span>
          </div>
        )}
      </div>
      <div className="space-y-3 mb-6 flex-grow">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start">
            <div className={`rounded-full p-1 ${feature.included ? 'text-success bg-success/10' : 'text-muted-foreground/50 bg-muted'}`}>
              <Check size={16} className={feature.included ? '' : 'opacity-50'} />
            </div>
            <span className={`text-sm ml-2 ${feature.included ? 'text-foreground' : 'text-muted-foreground/70 line-through'}`}>
              {feature.title}
            </span>
          </div>
        ))}
      </div>
      
      {usePaymentFlow ? (
        <PaymentFlowButton
          buttonText={buttonText}
          plan={getPlanType()}
          isPopular={isPopular}
          variant={isPopular ? 'default' : 'outline'}
        />
      ) : buttonLink ? (
        <Link to={buttonLink}>
          <Button 
            className={`w-full ${isPopular ? 'btn-gradient' : ''}`} 
            variant={isPopular ? 'default' : 'outline'}
          >
            {buttonText}
          </Button>
        </Link>
      ) : (
        <Button 
          className={`w-full ${isPopular ? 'btn-gradient' : ''}`} 
          variant={isPopular ? 'default' : 'outline'}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
};

export default PricingCard;
